import React, { FormEvent, useState } from 'react';
import { CheckBoxParam, Form, Row, SonForm, SubmitButton, Switch, useForm } from '../../../../_lib/Form2';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { FetchState } from '../../../../hooks/reducers/useFetchReducer';
import { dataPrintModeMarkerSelector } from '../../../../_pages/templates/Print/PrintTemplate';
import ModalDialog from '../../../../components/organisms/ModalDialog/ModalDialog';
import Portal from '../../../../components/atoms/Portal/Portal';
import { useNavigate } from 'react-router';

export type PagePrintTreeForm = SonForm<
  | CheckBoxParam<'enableComments'>
  | CheckBoxParam<'enableContextSection'>
  | CheckBoxParam<'withChildPages'>
  | CheckBoxParam<'allPageTypes'>
>;

export type PageInteractionsPrintPageForm = {
  closeModal: () => void;
  printTree: PagePrintTreeForm;
};

/**
 * Form to print the page via the global print '/baumdruck/' route.
 *
 * The route will be rendered in an iframe and the window.print method that iframe will print the pages.
 *  - iframe must be rendered at the end of the body with the same width as the portal to load images in correct width.
 */
export function PageInteractionsPrintPageForm({ closeModal, printTree }: PageInteractionsPrintPageForm) {
  const { t } = useSonTranslation('PageInteractions');
  const navigate = useNavigate();

  const [withChildPages, setWithChildPages] = useState<boolean>(false);
  const [iframeSrc, setIFrameSrc] = useState<string>();
  const [fetchState, setFetchState] = useState<FetchState<any, any>>({ type: 'initial' });

  const { params } = useForm({ form: printTree });

  const enableCommentsParam = params.getByName('enableComments');
  const enableContextSectionParam = params.getByName('enableContextSection');

  // withChildPages and enableAll not always available.
  const withChildPagesParam = params.findByName('withChildPages');
  const enableAllPageTypesParam = params.findByName('allPageTypes');

  /**
   * Print the Page tree on submit via loading an iframe and waiting for onload.
   * if iframe exists, call print on existing iframe to avoid loading iframe again.
   */
  const printPage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFetchState({ type: 'loading' });
    const formData = new FormData(event.target as any);
    const search = new URLSearchParams(formData as any);

    // using Array.from and length property to check for available search parameters as size parameter is
    // currently not available anymore in unit tests as of @babel update 7.23.6.
    // Check again when @babel is updated next time
    if (Array.from(search).length > 0) {
      setIFrameSrc(`${printTree.url}?${search.toString()}`);
    } else {
      setIFrameSrc(printTree.url);
    }
  };

  /**
   * This will check if the provided html contains the print mode marker.
   * this is to avoid printing error pages which can occur.
   */
  const checkIfHtmlIsOk = (iframeWindow: Window): boolean => {
    return !!iframeWindow.document.querySelector(dataPrintModeMarkerSelector);
  };

  return (
    <ModalDialog closeModal={closeModal} headline={t('print.modalHeadline')} initialFocusIndex={1}>
      <Form method="GET" action={printTree.url} onSubmit={printPage}>
        <Row>
          <Switch
            label={t('print.form.enableCommentsBlock')}
            name={enableCommentsParam.name}
            defaultValue={enableCommentsParam.value}
          />
        </Row>
        <Row>
          <Switch
            label={t('print.form.enableWidgetsInContext')}
            helpText={t('print.form.enableWidgetsInContextHelpText')}
            name={enableContextSectionParam.name}
            defaultValue={enableContextSectionParam.value}
          />
        </Row>
        {withChildPagesParam
          ? (
              <Row>
                <Switch
                  label={t('print.form.printPageTree')}
                  helpText={t('print.form.printPageTreeHelpText')}
                  name={withChildPagesParam.name}
                  defaultValue={withChildPagesParam.value}
                  onChange={event => setWithChildPages(event.target.checked)}
                />
              </Row>
            )
          : null}
        {withChildPages && enableAllPageTypesParam
          ? (
              <Row>
                <Switch
                  label={t('print.form.enableAllPageTypes')}
                  helpText={t('print.form.enableAllPageTypesHelpText')}
                  name={enableAllPageTypesParam.name}
                  defaultValue={enableAllPageTypesParam.value}
                />
              </Row>
            )
          : null}
        <Row modifier="no-margin">
          <SubmitButton
            buttonLabel={t('print.form.submitButton')}
            active={fetchState.type !== 'loading'}
            messageSuccess={t('print.form.messageSuccess')}
            messageSending={t('print.form.messageSending')}
            messageFail={t('print.form.messageFail')}
            fetchState={fetchState}
          />
        </Row>
      </Form>
      {iframeSrc && (
        // Portal required here to move iframe at document body
        <Portal>
          <div className="page-interactions__print-iframe-container">
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <iframe
              title={t('print.form.iframeTitle')}
              // important: invisible keeps SVG symbols printable in FF, no display none !
              className="page-interactions__print-iframe invisible"
              src={iframeSrc}
              onLoad={(element) => {
                const iFrameWindow = element.currentTarget.contentWindow;
                if (iFrameWindow && checkIfHtmlIsOk(iFrameWindow)) {
                  setFetchState({ type: 'success', response: null });

                  // close modal needs to be called afterprint
                  iFrameWindow.addEventListener('afterprint', () => {
                    // reset state to before print
                    setFetchState({ type: 'initial' });
                    setIFrameSrc('');

                    // reload page to avoid non responsive buttons due react having render problems
                    // after iframe for print was added see SON-18895 (quick fix)
                    navigate(0);
                    closeModal();
                  });

                  iFrameWindow.print();
                } else {
                  setFetchState({ type: 'error', requestError: null });
                }
              }}
            />
          </div>
        </Portal>
      )}
    </ModalDialog>
  );
}
